<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { LibraryBig } from '@lucide/vue'
import { Button, Description, Field, InlineAlert, Input, Label, Radio, RadioField, RadioGroup } from 'catalyst-vue'
import type { CollectionMode, CollectionSummary } from '../../lib/api-client'
import { useCloudLibraryStore } from '../../stores/cloud-library'
import { updateCollection } from '../../lib/collection-service'
import { useControlSize } from '../../lib/library-layout'
import CategoryPickerField from './category-picker-field.vue'
import CollectionCoverOption from './collection-cover-option.vue'
import LibraryOverlay from './library-overlay.vue'

/**
 * 編輯集合：標題、封面來源（從成員裡挑一本）、分類指派。
 * 三者一次送出（PATCH 只帶有改的欄位），成功後書庫重抓集合清單。
 */
const props = defineProps<{ collection: CollectionSummary | undefined }>()
const emit = defineEmits<{ close: []; done: [] }>()
const cloud = useCloudLibraryStore()
const controlSize = useControlSize()

const title = ref('')
const mode = ref<CollectionMode>('series')
const coverComicId = ref<string | null>(null)
const categoryIds = ref<string[]>([])
const busy = ref(false)
const error = ref('')
const open = computed(() => !!props.collection)
const members = computed(() => props.collection?.members ?? [])
/** 沒有指定封面來源時實際顯示的是第一個成員；選項上把它標成目前的。 */
const effectiveCover = computed(() => coverComicId.value ?? members.value[0]?.comicId ?? null)

watch(() => props.collection, collection => {
  if (!collection) return
  title.value = collection.title
  mode.value = collection.mode
  coverComicId.value = collection.coverComicId
  categoryIds.value = [...collection.categoryIds]
  error.value = ''
})

async function save() {
  const target = props.collection
  if (!target || busy.value) return
  const patch = {
    ...(title.value.trim() !== target.title ? { title: title.value } : {}),
    ...(mode.value !== target.mode ? { mode: mode.value } : {}),
    ...(coverComicId.value !== target.coverComicId ? { coverComicId: coverComicId.value } : {}),
    ...(sameSet(categoryIds.value, target.categoryIds) ? {} : { categoryIds: categoryIds.value }),
  }
  // 什麼都沒改就只是關掉：API 拒絕空的 PATCH，而「沒改」不是錯誤。
  if (!Object.keys(patch).length) { emit('close'); return }
  busy.value = true
  error.value = ''
  try {
    await updateCollection(target.id, patch)
    await cloud.refresh(true)
    emit('done')
    emit('close')
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : '集合尚未更新'
  } finally { busy.value = false }
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every(value => b.includes(value))
}
</script>

<template>
  <LibraryOverlay :open="open" title="編輯集合" description="標題、封面來源與分類。封面從成員的封面中挑一本。" :pending="busy" size="2xl" @close="emit('close')">
    <InlineAlert v-if="error" tone="error" class="mb-3">{{ error }}</InlineAlert>
    <form id="collection-edit-form" @submit.prevent="save">
      <Field>
        <Label>集合標題</Label>
        <Input v-model="title" :size="controlSize" :disabled="busy" />
      </Field>
      <Field class="mt-4">
        <Label>模式</Label>
        <RadioGroup v-model="mode" :disabled="busy">
          <RadioField>
            <Radio value="series" color="primary" />
            <Label>集合</Label>
            <Description>點進去列出成員，每本各自閱讀、各自有進度。</Description>
          </RadioField>
          <RadioField>
            <Radio value="merged" color="primary" />
            <Label>合併冊</Label>
            <Description>點下去直接開閱讀器，成員依順序串成一本連續閱讀；進度與書籤仍記在各成員上，切換模式不會遺失。</Description>
          </RadioField>
        </RadioGroup>
      </Field>
      <fieldset class="collection-cover-options mt-4">
        <legend>封面來源</legend>
        <ul class="collection-cover-list">
          <li v-for="member in members" :key="member.comicId">
            <CollectionCoverOption
              :member="member"
              :current="member.comicId === effectiveCover"
              :disabled="busy"
              @choose="coverComicId = member.comicId"
            />
          </li>
        </ul>
        <p v-if="!members.length" class="collection-cover-empty"><LibraryBig :size="16" aria-hidden="true" /> 這個集合目前沒有成員。</p>
      </fieldset>
      <CategoryPickerField v-model="categoryIds" class="mt-4" :categories="cloud.categories" :disabled="busy" legend="集合的分類" />
    </form>
    <template #actions>
      <Button outline :disabled="busy" @click="emit('close')">取消</Button>
      <Button color="primary" type="submit" form="collection-edit-form" :disabled="busy || !title.trim()">{{ busy ? '正在儲存…' : '儲存' }}</Button>
    </template>
  </LibraryOverlay>
</template>
